import { Check, CircleSlash, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { serviceRequestPhaseSteps, type StepState } from '../../lib/service-requests';

const CIRCLE_TONE: Record<StepState, string> = {
  done: 'border-primary bg-primary text-primary-foreground',
  current: 'border-primary bg-white text-primary ring-4 ring-primary/15 dark:bg-slate-900',
  todo: 'border-slate-200 bg-white text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500',
  stopped: 'border-rose-500 bg-rose-500 text-white',
};

export type Step = { key: string; label: string; hint?: string | null; state: StepState };

/**
 * Numbered progress rail for the request lifecycle.
 * Purely presentational: the caller decides which step is where.
 */
export function Steps({ steps, className }: { steps: readonly Step[]; className?: string }) {
  return (
    <ol className={cn('flex items-start', className)} aria-label="Progress">
      {steps.map((step, index) => (
        <li key={step.key} className="flex min-w-0 flex-1 items-start last:flex-none">
          <div className="flex min-w-0 flex-col items-center gap-1 px-1 sm:flex-row sm:items-center sm:gap-2">
            <span
              aria-hidden
              className={cn(
                'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums transition-colors',
                CIRCLE_TONE[step.state],
              )}
            >
              {step.state === 'done' ? <Check className="h-4 w-4" /> : step.state === 'stopped' ? <X className="h-4 w-4" /> : index + 1}
            </span>
            <span className="min-w-0 text-center sm:text-left">
              <span
                className={cn(
                  'block truncate text-xs font-medium sm:text-sm',
                  step.state === 'todo' ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {step.label}
              </span>
              {step.hint ? <span className="block truncate text-[11px] text-muted-foreground">{step.hint}</span> : null}
            </span>
          </div>
          {index < steps.length - 1 ? (
            <span
              aria-hidden
              className={cn(
                'mt-3.5 h-px min-w-[0.75rem] flex-1 rounded-full',
                steps[index + 1].state === 'todo' || steps[index + 1].state === 'stopped' ? 'bg-border' : 'bg-primary',
              )}
            />
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/** Where the request sits in the Request → Approval → Delivery → Deployment → Validation lifecycle. */
export function StageProgress({
  stage,
  stateName,
  className,
}: {
  stage: string | null | undefined;
  stateName?: string | null;
  className?: string;
}) {
  const steps = serviceRequestPhaseSteps(stage, stateName);
  const stopped = stage === 'rejected' || stage === 'cancelled';
  return (
    <div className={cn('rounded-md border bg-card px-4 py-3 text-card-foreground', className)}>
      <Steps steps={steps} />
      {stopped ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-destructive">
          <CircleSlash className="h-3.5 w-3.5" aria-hidden />
          This request is {stage === 'rejected' ? 'rejected' : 'cancelled'} and no longer moves through the lifecycle.
        </p>
      ) : null}
    </div>
  );
}
